import { useState } from 'react';
import { ChevronRight, Trash2, Edit3, RefreshCw, ArrowRightLeft, Share2, Loader2, Sparkles, Users } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

export function JobTable({
  jobs, loading, submissionCounts,
  onJobClick, onEdit, onDelete, onRepost, onTransfer, onOpenPicker,
  repostingId, pickerJobId, sourceBtnRefs
}) {
  const { addToast } = useToast();

  const copyPublicLink = (e, job) => {
    e.stopPropagation();
    const url = `${window.location.origin}/apply/${job.job_id}`;
    navigator.clipboard?.writeText(url).then(() => {
      addToast('Public apply link copied!', 'success');
    }).catch(() => addToast('Could not copy link', 'error'));
  };

  const PIPELINE_STAGES = ['applied', 'screened', 'submitted', 'interview', 'offer'];
  const STAGE_COLORS = {
    applied:   'bg-slate-100 text-slate-600',
    screened:  'bg-blue-100 text-blue-700',
    submitted: 'bg-amber-100 text-amber-700',
    interview: 'bg-purple-100 text-purple-700',
    offer:     'bg-emerald-100 text-emerald-700',
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1,2,3].map(i => (
          <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
            <div className="h-5 bg-slate-100 rounded w-1/3 mb-3" />
            <div className="h-3 bg-slate-100 rounded w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  if (!jobs || jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-blue-300" />
        </div>
        <h3 className="text-lg font-semibold text-slate-600 mb-2">No job roles yet</h3>
        <p className="text-slate-400 text-sm">Create your first job role to start sourcing candidates.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {jobs.map(job => {
        const counts = submissionCounts?.[job.job_id] || {};
        const totalSubs = PIPELINE_STAGES.reduce((sum, s) => sum + (counts[s] || 0), 0);
        const wm = job.work_mode || 'remote';
        const wmColors = { remote: 'text-emerald-600 bg-emerald-50', hybrid: 'text-amber-600 bg-amber-50', onsite: 'text-blue-600 bg-blue-50' };

        return (
          <div key={job.job_id}
            className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => onJobClick(job)}
            data-testid={`job-row-${job.job_id}`}>
            <div className="flex items-start justify-between gap-4">
              {/* Left: Title + meta */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-bold text-slate-800 truncate">{job.title}</h3>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    job.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    job.status === 'paused' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-slate-100 text-slate-500 border-slate-200'
                  }`}>{job.status}</span>
                  {job.job_type && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{job.job_type}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  {job.client && <span className="font-medium text-slate-600">{job.client}</span>}
                  {(job.location_city || job.location_state) && (
                    <span>{[job.location_city, job.location_state].filter(Boolean).join(', ')}</span>
                  )}
                  <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-medium ${wmColors[wm] || wmColors.remote}`}>{wm}</span>
                  <span>{job.candidate_count || 0} candidates</span>
                  {totalSubs > 0 && <span className="text-blue-600 font-medium">{totalSubs} in pipeline</span>}
                </div>

                {/* Pipeline stage mini-badges */}
                {totalSubs > 0 && (
                  <div className="flex items-center gap-1.5 mt-2">
                    {PIPELINE_STAGES.map(stage => {
                      const count = counts[stage] || 0;
                      if (count === 0) return null;
                      return (
                        <span key={stage} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STAGE_COLORS[stage]}`}>
                          {stage} {count}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right: Action buttons */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                {/* Source candidates */}
                <button
                  ref={el => { if (sourceBtnRefs?.current) sourceBtnRefs.current[job.job_id] = el; }}
                  onClick={(e) => onOpenPicker(e, job.job_id)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                    pickerJobId === job.job_id ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                  title="Source candidates"
                  data-testid={`source-btn-${job.job_id}`}>
                  <Sparkles className="w-3.5 h-3.5" /> Source
                </button>

                {/* Share public apply link */}
                <button onClick={(e) => copyPublicLink(e, job)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                  title="Copy public apply link">
                  <Share2 className="w-4 h-4" />
                </button>

                {/* Edit JD */}
                <button onClick={(e) => { e.stopPropagation(); onEdit(job); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                  title="Edit job description">
                  <Edit3 className="w-4 h-4" />
                </button>

                {/* Repost */}
                <button onClick={(e) => onRepost(e, job.job_id)} disabled={repostingId === job.job_id}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors disabled:opacity-40"
                  title="Repost job">
                  {repostingId === job.job_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>

                {/* Transfer */}
                <button onClick={(e) => { e.stopPropagation(); onTransfer(job); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                  title="Transfer ownership">
                  <ArrowRightLeft className="w-4 h-4" />
                </button>

                {/* Delete */}
                <button onClick={(e) => { e.stopPropagation(); onDelete(job.job_id); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Archive job">
                  <Trash2 className="w-4 h-4" />
                </button>

                <ChevronRight className="w-4 h-4 text-slate-300 ml-1" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
